import { Link } from "@inertiajs/react";
import { motion } from "motion/react";

import { ProductCard } from "@/theme2/components/product/ProductCard";
import MoveToCartButton from "@/theme2/components/wishlist/MoveToCartButton";
import RemoveWishlistButton from "@/theme2/components/wishlist/RemoveWishlistButton";

export default function WishlistGrid({
  products,
  view,
  onRemove,
  onMoveToCart,
}) {
  if (view === "list") {
    return (
      <motion.div layout className="space-y-5">
        {products.map((product, index) => (
          <motion.article
            key={product.slug}
            layout
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.97 }}
            transition={{ duration: 0.35, delay: index * 0.04 }}
            className="grid gap-5 rounded-[1.75rem] border border-border/60 bg-white p-4 shadow-xl shadow-black/5 sm:p-5 md:grid-cols-[11rem_1fr] lg:grid-cols-[12rem_1fr_15rem] lg:items-center"
          >
            <Link
              href={`/products/${product.slug}`}
              className="group relative aspect-[4/5] overflow-hidden rounded-[1.4rem] bg-neutral-100"
            >
              <img
                src={
                  product.image ||
                  product.gallery?.[0]?.src ||
                  product.images?.[0]?.src ||
                  product.images?.[0]
                }
                alt={product.title}
                className="size-full object-cover transition duration-500 group-hover:scale-105"
              />
            </Link>

            <div className="min-w-0">
              <p className="text-xs font-semibold uppercase tracking-[0.18em] text-muted-foreground">
                {product.series || product.category || "Captex"}
              </p>

              <Link
                href={`/products/${product.slug}`}
                className="mt-2 block text-xl font-black tracking-tight text-foreground transition hover:text-muted-foreground sm:text-2xl"
              >
                {product.title}
              </Link>

              <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-2 text-sm text-muted-foreground">
                {product.rating != null && (
                  <span>★ {product.rating}</span>
                )}

                {product.reviews != null && (
                  <span>
                    {product.reviews}{" "}
                    {product.reviews === 1 ? "review" : "reviews"}
                  </span>
                )}

                {product.isSoldOut ? (
                  <span className="font-semibold text-red-600">Sold Out</span>
                ) : (
                  <span className="font-semibold text-emerald-600">
                    In Stock
                  </span>
                )}
              </div>

              <div className="mt-5 flex flex-wrap items-end gap-3">
                <span className="text-2xl font-black">
                  ৳{Number(product.price || 0).toLocaleString()}
                </span>

                {(product.comparePrice || product.compareAtPrice) && (
                  <span className="text-sm text-muted-foreground line-through">
                    ৳
                    {Number(
                      product.comparePrice || product.compareAtPrice
                    ).toLocaleString()}
                  </span>
                )}

                {product.discount && (
                  <span className="rounded-full bg-red-50 px-3 py-1 text-xs font-black uppercase text-red-600">
                    {product.discount}% off
                  </span>
                )}
              </div>
            </div>

            <div className="grid gap-3 sm:grid-cols-2 md:col-span-2 lg:col-span-1 lg:grid-cols-1">
              <MoveToCartButton
                product={product}
                onMoveToCart={onMoveToCart}
              />

              <RemoveWishlistButton
                product={product}
                onRemove={onRemove}
              />
            </div>
          </motion.article>
        ))}
      </motion.div>
    );
  }

  return (
    <motion.div
      layout
      className="grid gap-5 sm:grid-cols-2 lg:grid-cols-4"
    >
      {products.map((product, index) => (
        <motion.div
          key={product.slug}
          layout
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.96 }}
          transition={{ duration: 0.35, delay: index * 0.04 }}
          className="rounded-[1.75rem] border border-border/60 bg-white p-3 shadow-xl shadow-black/5"
        >
          <ProductCard product={product} />

          <div className="mt-3 grid gap-2 sm:grid-cols-2">
            <MoveToCartButton
              product={product}
              onMoveToCart={onMoveToCart}
            />

            <RemoveWishlistButton
              product={product}
              onRemove={onRemove}
            />
          </div>
        </motion.div>
      ))}
    </motion.div>
  );
}